import { bodyTypeForApi, normalizeRequestBodyMode } from './requestBodyMode';
import { makeKvRowId } from './kvRows';

/** @typedef {{ key: string, value?: string, type?: 'text'|'file', enabled?: boolean, filePath?: string|null, _id?: string }} FormDataField */

export function defaultFormDataFields() {
  return [{ key: '', value: '', type: 'text', enabled: true, filePath: null }];
}

export function normalizeFormDataFields(fields) {
  if (!Array.isArray(fields) || fields.length === 0) {
    return defaultFormDataFields();
  }
  return fields.map((row) => ({
    key: String(row?.key ?? ''),
    value: String(row?.value ?? ''),
    type: row?.type === 'file' ? 'file' : 'text',
    enabled: row?.enabled !== false,
    filePath: row?.filePath ?? null,
    // Display-only echo of the path an imported collection claimed (COL-01).
    // Never becomes `filePath` and is never sent — it exists so the file chip
    // can tell the user which file the collection's author had attached.
    ...(row?.filePathHint ? { filePathHint: String(row.filePathHint) } : {}),
    _id: row?._id,
  }));
}

/** UI body tab id → persisted body_type (legacy callers; prefer bodyTypeForApi). */
export function bodyTypeFromUi(uiType, rawBodyLanguage) {
  const { bodyType, rawBodyLanguage: lang } = normalizeRequestBodyMode({
    bodyType: uiType,
    rawBodyLanguage,
  });
  return bodyTypeForApi(bodyType, lang);
}

/** Persisted body_type → UI body tab id (legacy; use normalizeRequestBodyMode). */
export function bodyTypeToUi(persistedType, settings) {
  return normalizeRequestBodyMode({ bodyType: persistedType, settings }).bodyType;
}

export function getFormDataFieldsFromRequest(source) {
  const fromSettings = source?.settings?.form_data_fields;
  if (Array.isArray(fromSettings) && fromSettings.length > 0) {
    return normalizeFormDataFields(fromSettings);
  }
  if (Array.isArray(source?.formDataFields) && source.formDataFields.length > 0) {
    return normalizeFormDataFields(source.formDataFields);
  }
  // Imported urlencoded requests (HAR/Bruno/OpenAPI) carry a serialized query
  // string in `body` with no explicit field rows — derive rows so the runner
  // and editor have something to send (disabled state can't be expressed here).
  const { bodyType } = normalizeRequestBodyMode(source);
  if (bodyType === 'urlencoded' && typeof source?.body === 'string' && source.body) {
    const rows = [];
    for (const [key, value] of new URLSearchParams(source.body).entries()) {
      rows.push({ key, value, type: 'text', enabled: true, filePath: null });
    }
    if (rows.length > 0) return normalizeFormDataFields(rows);
  }
  return defaultFormDataFields();
}

export function mergeFormDataIntoSettings(settings, formDataFields, bodyType) {
  const next = { ...(settings || {}) };
  const ui = bodyType === 'form-data' ? 'form-data' : bodyType;
  const persisted = bodyTypeFromUi(ui);
  if (persisted === 'formdata' || persisted === 'urlencoded') {
    const rows = stripBlankFormDataFields(formDataFields);
    if (rows.length > 0) {
      next.form_data_fields = rows.map(({ key, value, type, enabled, filePath, filePathHint }) => ({
        key,
        value: value ?? '',
        type: persisted === 'urlencoded' ? 'text' : (type === 'file' ? 'file' : 'text'),
        enabled: enabled !== false,
        filePath: persisted === 'urlencoded' ? null : (filePath ?? null),
        ...(persisted !== 'urlencoded' && filePathHint ? { filePathHint } : {}),
      }));
    } else {
      delete next.form_data_fields;
    }
  } else {
    delete next.form_data_fields;
  }
  return next;
}

export function stripBlankFormDataFields(fields) {
  return normalizeFormDataFields(fields).filter((row) => {
    if (!row.key?.trim()) return false;
    if (row.type === 'file') return Boolean(row.filePath);
    return true;
  });
}

/** Editor row is meaningful if it has a key/value or the user picked File before entering a key. */
export function isNonBlankFormDataEditorRow(row) {
  if (String(row?.key ?? '').trim() !== '') return true;
  if (String(row?.value ?? '').trim() !== '') return true;
  if (row?.type === 'file') return true;
  return false;
}

export function stripBlankFormDataEditorRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter(isNonBlankFormDataEditorRow);
}

/** Trailing empty key row for form-data / urlencoded editors (preserves type: file rows). */
export function ensureTrailingEmptyFormDataRow(rows) {
  const cleaned = stripBlankFormDataEditorRows(rows);
  const base =
    cleaned.length > 0
      ? cleaned.map((row) => ({
          ...row,
          type: row?.type === 'file' ? 'file' : 'text',
          filePath: row?.filePath ?? null,
        }))
      : [{ key: '', value: '', type: 'text', enabled: true, filePath: null, _id: `fd-0-${Date.now()}` }];
  const last = base[base.length - 1];
  if (last.key !== '') {
    base.push({
      key: '',
      value: '',
      type: 'text',
      enabled: true,
      filePath: null,
      _id: makeKvRowId('fd'),
    });
  } else if (last.type == null) {
    last.type = 'text';
    last.filePath = last.filePath ?? null;
  }
  return base;
}

/**
 * Build outbound multipart parts for Tauri / browser send.
 * @param {FormDataField[]} fields
 * @param {(s: string) => string} substituteVariables
 */
export function buildMultipartParts(fields, substituteVariables = (s) => s) {
  const parts = [];
  for (const row of stripBlankFormDataFields(fields)) {
    if (row.enabled === false) continue;
    const name = substituteVariables(row.key).trim();
    if (!name) continue;
    if (row.type === 'file') {
      if (row.filePath) {
        parts.push({ name, file_path: row.filePath });
      }
    } else {
      parts.push({ name, text: substituteVariables(row.value ?? '') });
    }
  }
  return parts;
}
